import { useId } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface FilterMultiSelectItem {
  value: string;
  label: string;
  /** Shown beside a disabled option so the reason is visible without hovering. */
  disabledReason?: string;
}

interface FilterMultiSelectProps {
  ariaLabel: string;
  placeholder: string;
  items: readonly FilterMultiSelectItem[];
  value: readonly string[];
  onValueChange: (value: string[]) => void;
  /** Disables the whole control; the reason opens as a tooltip on hover. */
  disabledReason?: string;
  /** Label for the count of further selections, e.g. "+3". */
  moreLabel: (count: number) => string;
  className?: string;
}

/**
 * Multi-value companion to FilterSelect for data-table toolbars. FilterSelect
 * models "one value, first item = all"; a multi-select has no "all" item and an
 * empty selection means no filter, so it is a separate control rather than a
 * mode of FilterSelect.
 */
export function FilterMultiSelect({
  ariaLabel,
  placeholder,
  items,
  value,
  onValueChange,
  disabledReason,
  moreLabel,
  className,
}: FilterMultiSelectProps): React.ReactElement {
  const reasonId = useId();
  const labelOf = (key: string) => items.find((item) => item.value === key)?.label ?? key;
  const isApplied = value.length > 0;

  const control = (
    <Select
      multiple
      items={items}
      value={[...value]}
      onValueChange={(next) => onValueChange(next)}
      disabled={!!disabledReason}
    >
      <SelectTrigger
        size="sm"
        aria-label={ariaLabel}
        aria-describedby={disabledReason ? reasonId : undefined}
        data-filter-applied={isApplied || undefined}
        className={cn(
          "min-w-[128px] gap-[7px] px-2.5",
          isApplied && "border-transparent bg-control-highlight",
          className,
        )}
      >
        <SelectValue>
          {(selected: string[]) =>
            selected.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate">{labelOf(selected[0] ?? "")}</span>
                {selected.length > 1 && (
                  <span className="shrink-0 rounded-sm bg-muted px-1 text-[10px] leading-4 text-muted-foreground">
                    {moreLabel(selected.length - 1)}
                  </span>
                )}
              </span>
            )
          }
        </SelectValue>
      </SelectTrigger>
      <SelectContent align="start" className="w-auto min-w-52">
        <SelectGroup>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value} disabled={!!item.disabledReason}>
              {item.label}
              {item.disabledReason && (
                <span className="text-[10px] text-muted-foreground">{item.disabledReason}</span>
              )}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );

  if (!disabledReason) return control;
  // A disabled trigger swallows pointer events, so the wrapper opens the
  // tooltip on hover; screen readers get the reason via aria-describedby.
  return (
    <Tooltip>
      <TooltipTrigger render={<span className="inline-flex" />}>{control}</TooltipTrigger>
      <TooltipContent>{disabledReason}</TooltipContent>
      <span id={reasonId} className="sr-only">
        {disabledReason}
      </span>
    </Tooltip>
  );
}
